import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Element } from "react-scroll";
import { IconType } from "react-icons";
import {
    TbBulb, TbCamera, TbCircuitSwitchOpen, TbDeviceLaptop, TbDeviceMobile, TbDeviceTv,
    TbDeviceWatch, TbDevices, TbElevator, TbHome, TbPlug, TbScale, TbToolsKitchen2, TbWind,
} from "react-icons/tb";

import { Title } from "src/components";
import { colors } from "src/constants/colors";


const servicesData = (t: TFunction): { title: string; description: string; icon: IconType }[] => [
    {
        title: t("services.items.electronics_import.title"),
        description: t("services.items.electronics_import.description"),
        icon: TbDevices,
    },
    {
        title: t("services.items.air_purification.title"),
        description: t("services.items.air_purification.description"),
        icon: TbWind,
    },
    {
        title: t("services.items.restaurants_hotels.title"),
        description: t("services.items.restaurants_hotels.description"),
        icon: TbToolsKitchen2,
    },
    {
        title: t("services.items.elevators.title"),
        description: t("services.items.elevators.description"),
        icon: TbElevator,
    },
    {
        title: t("services.items.wires_switches_lights.title"),
        description: t("services.items.wires_switches_lights.description"),
        icon: TbBulb,
    },
    {
        title: t("services.items.electric_panels.title"),
        description: t("services.items.electric_panels.description"),
        icon: TbCircuitSwitchOpen,
    },
    {
        title: t("services.items.electrical_accessories.title"),
        description: t("services.items.electrical_accessories.description"),
        icon: TbPlug,
    },
    {
        title: t("services.items.audio_visual.title"),
        description: t("services.items.audio_visual.description"),
        icon: TbDeviceTv,
    },
    {
        title: t("services.items.phones.title"),
        description: t("services.items.phones.description"),
        icon: TbDeviceMobile,
    },
    {
        title: t("services.items.photography_equipment.title"),
        description: t("services.items.photography_equipment.description"),
        icon: TbCamera,
    },
    {
        title: t("services.items.computers.title"),
        description: t("services.items.computers.description"),
        icon: TbDeviceLaptop,
    },
    {
        title: t("services.items.watches_glasses.title"),
        description: t("services.items.watches_glasses.description"),
        icon: TbDeviceWatch,
    },
    {
        title: t("services.items.measuring_tools.title"),
        description: t("services.items.measuring_tools.description"),
        icon: TbScale,
    },
    {
        title: t("services.items.home_supplies.title"),
        description: t("services.items.home_supplies.description"),
        icon: TbHome,
    }
];

const Services = () => {
    const { t } = useTranslation("home");
    const services = servicesData(t);

    return (
        <Element name="services" className="relative overflow-hidden bg-[#76b8ee0e] py-20 sm:py-24 lg:py-28">
            {/* soft background accents */}
            <div aria-hidden className="pointer-events-none absolute -top-40 -start-40 h-96 w-96 rounded-full bg-primary-100/60 blur-3xl" />
            <div aria-hidden className="pointer-events-none absolute -bottom-40 -end-40 h-96 w-96 rounded-full bg-primary-100/50 blur-3xl" />

            <div className="container relative">
                <div data-aos="fade-up">
                    <Title
                        lgTitle={t("services.sub_title")}
                        smTitle={t("services.title")}
                        lgTitleColor={colors.heading}
                        smTitleColor={colors.primary}
                    />
                </div>

                <div className="mt-12 grid grid-cols-1 gap-5 sm:mt-14 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
                    {services.map((item, index) => (
                        <div
                            key={item.title}
                            data-aos="fade-up"
                            data-aos-delay={(index % 3) * 80}
                            className="group relative flex h-full flex-col items-center overflow-hidden rounded-3xl bg-white px-6 pb-8 pt-9 text-center shadow-card ring-1 ring-primary-50 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-card-hover hover:ring-primary-100 sm:px-7"
                        >
                            {/* top accent bar */}
                            <span aria-hidden className="absolute inset-x-0 top-0 h-1 origin-center scale-x-0 bg-gradient-to-r from-primary-300 via-primary-400 to-dark-primary-500 transition-transform duration-500 group-hover:scale-x-100" />
                            {/* index number */}
                            <span aria-hidden className="absolute end-5 top-4 text-sm font-semibold tabular-nums text-primary-100 transition-colors group-hover:text-primary-200">
                                {String(index + 1).padStart(2, "0")}
                            </span>

                            <div className="relative mb-6">
                                <div aria-hidden className="absolute inset-0 rotate-6 rounded-2xl bg-primary-100 transition-transform duration-500 group-hover:rotate-12" />
                                <div className="relative grid h-20 w-20 place-items-center rounded-2xl bg-gradient-to-br from-primary-50 to-white text-dark-primary-500 ring-1 ring-primary-100 transition-all duration-500 group-hover:from-primary-400 group-hover:to-dark-primary-500 group-hover:text-white group-hover:ring-transparent">
                                    <item.icon className="h-10 w-10" strokeWidth={1.4} />
                                </div>
                            </div>

                            <h3 className="mb-3 text-base font-normal leading-[1.75] tracking-brand text-gray-900 sm:text-lg">
                                {item.title}
                            </h3>
                            <p className="text-sm font-light leading-[1.9] tracking-[0.5px] text-body sm:text-[0.9375rem]">
                                {item.description}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </Element>
    );
};

export default Services;
